import type { RoomMember } from 'matrix-js-sdk';

/**
 * Extrahiert die UUID aus einer Matrix User-ID
 * Format: @uuid:domain -> uuid
 */
export function extractUuidFromUserId(userId: string): string {
  return userId.split(':')[0].slice(1);
}

/**
 * Normalisiert einen Display-Namen durch Entfernen von mehrfachen Leerzeichen
 */
export function normalizeDisplayName(name: string): string {
  return name.replace(/\s+/g, ' ').trim();
}

/**
 * Generiert Initialen aus einem Display-Namen
 * - Bei zwei Wörtern: Erste Buchstaben beider Wörter
 * - Sonst: Erste zwei Buchstaben
 */
export function getInitialsFromName(name: string): string {
  if (!name) return 'NN';

  const normalized = normalizeDisplayName(name);
  const parts = normalized.split(' ');

  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }

  return normalized.slice(0, 2).toUpperCase();
}

/**
 * Generiert Initialen aus einem RoomMember
 */
export function getInitialsFromMember(roomMember: RoomMember | undefined): string {
  if (!roomMember?.rawDisplayName) return 'NN';
  return getInitialsFromName(roomMember.rawDisplayName);
}

/**
 * Mappt RoomMembers zu einem einfacheren Format mit UUID und Display-Namen
 */
export function mapMembersToProfiles(members: RoomMember[]): Array<{
  uuid: string;
  matrixUserId: string;
  displayName: string;
}> {
  return members.map((member) => ({
    uuid: extractUuidFromUserId(member.userId),
    matrixUserId: member.userId,
    displayName: member.rawDisplayName,
  }));
}

/**
 * Erstellt eine Map von UUID zu Profile für schnellere Lookups
 */
export function createProfileMap<T extends { uuid: string }>(profiles: T[]): Map<string, T> {
  return new Map(profiles.map((profile) => [profile.uuid, profile]));
}
